import { Box, Button, Flex, Popover, PopoverBody, PopoverContent, PopoverTrigger, Stack, Text } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { TestSelectors } from '@resistapp/common/testing/visual-test-selectors';
import { FullProject, XAxisMode } from '@resistapp/common/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GroupChildContainer } from '../../containers/group-child-container';
import { SettingsIcon } from '../../icons/settings-icon';
import { UndoIcon } from '../../icons/undo-icon';
import { ChartGroupContainer } from '../../plots/containers/chart-group-container';
import { GeneLegendFilter } from '../../plots/legends/gene-legend-filter';
import { SampleAndTypeSearch } from '../../plots/legends/sample-search';
import { theme } from '../../shared/theme';
import { NormalisationModeSelector } from './normalisation-mode-selector';
import { TimeFilterSelector } from './time-filter-selector';
import { XAxisModeSelector } from './x-axis-mode-selector';

interface Props {
  style?: React.CSSProperties;
  project: FullProject | null;
  innerRef?: React.RefObject<HTMLDivElement>;
}

// Define props type for styled components that use the theme
interface ContainerWithThemeProps {
  theme: typeof theme;
}

export enum FilterBarElementKey {
  GENE = 'GENE',
  SAMPLE = 'SAMPLE',
  TIME = 'TIME',
  NORMALISATION = 'NORMALISATION',
  X_AXIS = 'X_AXIS',
}

// The order in which filters should occupy inline space (left-to-right)
const filterDisplayOrder: FilterBarElementKey[] = [
  FilterBarElementKey.GENE,
  FilterBarElementKey.SAMPLE,
  FilterBarElementKey.TIME,
  FilterBarElementKey.NORMALISATION,
  FilterBarElementKey.X_AXIS,
];

// Hard-coded widths (in px) for each filter in both modes
const FILTER_WIDTHS: Record<FilterBarElementKey, { full: number; narrow: number }> = {
  [FilterBarElementKey.GENE]: { full: 208, narrow: 160 },
  [FilterBarElementKey.SAMPLE]: { full: 250, narrow: 200 },
  [FilterBarElementKey.TIME]: { full: 205, narrow: 160 },
  [FilterBarElementKey.NORMALISATION]: { full: 233, narrow: 170 },
  [FilterBarElementKey.X_AXIS]: { full: 260, narrow: 170 },
};

export function FilterBar(props: Props) {
  const { queryFilters, availableXAxisModes } = useSampleDataContext();

  // Determine which filter keys are available given current data
  const availableSelectorKeys = useMemo<FilterBarElementKey[]>(() => {
    const keys: FilterBarElementKey[] = [FilterBarElementKey.GENE];
    if (props.project) keys.push(FilterBarElementKey.SAMPLE);
    if (availableXAxisModes.includes(XAxisMode.TIME)) keys.push(FilterBarElementKey.TIME);
    keys.push(FilterBarElementKey.NORMALISATION);
    // Only show X-axis selector when there is more than one possible mode to choose from.
    if (availableXAxisModes.length > 1) {
      keys.push(FilterBarElementKey.X_AXIS);
    }
    return keys;
  }, [props.project, availableXAxisModes]);

  // Helper to build element for a given key
  const buildSelectElementForKey = useCallback(
    (key: FilterBarElementKey, compact: boolean): JSX.Element => {
      switch (key) {
        case FilterBarElementKey.GENE:
          return <GeneLegendFilter queryFilters={queryFilters} />;
        case FilterBarElementKey.SAMPLE:
          return props.project ? <SampleAndTypeSearch queryFilters={queryFilters} project={props.project} /> : <></>;
        case FilterBarElementKey.TIME:
          return <TimeFilterSelector />;
        case FilterBarElementKey.NORMALISATION:
          return <NormalisationModeSelector queryFilters={queryFilters} compact={compact} />;
        case FilterBarElementKey.X_AXIS:
          return <XAxisModeSelector queryFilters={queryFilters} compact={compact} />;
        default:
          return <></>;
      }
    },
    [queryFilters, props.project],
  );

  /*
   * ----------------------------------------------------------------------
   * Responsive layout handling
   * ----------------------------------------------------------------------
   * Instead of relying on Chakra breakpoints we calculate how many filters can
   * fit into the available horizontal space. Every filter has two fixed
   * widths: a "full" width (default) and a slightly narrower width that we
   * try before moving the filter into the overflow menu.  This guarantees
   * that elements never overflow while still using the maximum space that
   * is available.
   */
  const GAP_BETWEEN_FILTERS = 20; // Must match the Flex gap="20px"
  const SETTINGS_ICON_WIDTH = 45;
  const RESET_BUTTON_MIN_WIDTH = 40; // icon-only variant
  const RESET_BUTTON_FULL_WIDTH = 90; // icon + text variant (approx.)
  const STATIC_ELEMENTS_WIDTH = SETTINGS_ICON_WIDTH + RESET_BUTTON_MIN_WIDTH;

  // Expose the DOM node to the outside if caller supplied a ref
  const containerRef = useRef<HTMLDivElement | null>(null);
  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (props.innerRef) {
        (props.innerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [props.innerRef],
  );

  // Track container width using ResizeObserver
  const [containerWidth, setContainerWidth] = useState<number>(0);
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    // Trigger initial measurement
    setContainerWidth(node.getBoundingClientRect().width);
    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });
    resizeObserver.observe(node);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Determine which filters to show inline and which to show in the overflow menu
  const { inlineSelectorKeys, selectorWidthModes } = useMemo<{
    inlineSelectorKeys: FilterBarElementKey[];
    selectorWidthModes: Partial<Record<FilterBarElementKey, 'full' | 'narrow'>>;
  }>(() => {
    const orderedKeys: FilterBarElementKey[] = filterDisplayOrder.filter((key): key is FilterBarElementKey =>
      availableSelectorKeys.includes(key),
    );

    const availableWidth = Math.max(0, containerWidth - STATIC_ELEMENTS_WIDTH - GAP_BETWEEN_FILTERS);

    // Early exit - unknown width: show gene filter only (legacy behaviour)
    if (availableWidth === 0) {
      return {
        inlineSelectorKeys: [FilterBarElementKey.GENE],
        selectorWidthModes: { [FilterBarElementKey.GENE]: 'full' },
      };
    }

    const modesMap: Partial<Record<FilterBarElementKey, 'full' | 'narrow' | undefined>> = {};
    orderedKeys.forEach((k: FilterBarElementKey) => {
      modesMap[k] = 'full';
    });

    const workinginlineSelectorKeys: FilterBarElementKey[] = [...orderedKeys];

    const computeTotalWidth = (): number => {
      if (!workinginlineSelectorKeys.length) return 0;
      return workinginlineSelectorKeys.reduce((sum: number, key: FilterBarElementKey, idx: number) => {
        const currentMode = modesMap[key] === 'narrow' ? 'narrow' : 'full';
        const keyWidth = FILTER_WIDTHS[key][currentMode];
        const gaps = idx === 0 ? 0 : GAP_BETWEEN_FILTERS;
        return sum + keyWidth + gaps;
      }, 0);
    };

    while (computeTotalWidth() > availableWidth) {
      // Find the last inline key that is still full-width.
      const keyToCompact = [...workinginlineSelectorKeys]
        .reverse()
        .find((k: FilterBarElementKey) => modesMap[k] === 'full');

      if (keyToCompact) {
        modesMap[keyToCompact] = 'narrow';
        continue; // re-evaluate width after compacting one filter
      }

      // If all inline filters are already compact, start moving filters to overflow.
      const removedKey = workinginlineSelectorKeys.pop();
      if (removedKey) {
        modesMap[removedKey] = undefined;
      }

      // Ensure the gene filter is always present.
      if (!workinginlineSelectorKeys.includes(FilterBarElementKey.GENE)) {
        workinginlineSelectorKeys.unshift(FilterBarElementKey.GENE);
        modesMap[FilterBarElementKey.GENE] = modesMap[FilterBarElementKey.GENE] === 'narrow' ? 'narrow' : 'full';
      }

      // If only the gene filter remains compact & still doesn't fit, break to avoid infinite loop.
      if (workinginlineSelectorKeys.length === 1 && computeTotalWidth() > availableWidth) {
        break;
      }
    }

    return { inlineSelectorKeys: workinginlineSelectorKeys, selectorWidthModes: modesMap };
  }, [availableSelectorKeys, containerWidth, STATIC_ELEMENTS_WIDTH]);

  const inlineFilters = inlineSelectorKeys.map((key: FilterBarElementKey) => ({
    key,
    element: buildSelectElementForKey(key, selectorWidthModes[key] === 'narrow'),
  }));
  const overflowKeys = availableSelectorKeys.filter((key: FilterBarElementKey) => !inlineSelectorKeys.includes(key));
  const overflowFilters = overflowKeys.map((key: FilterBarElementKey) => ({
    key,
    element: buildSelectElementForKey(key, false),
  }));

  const getInlineFilterWidth = (key: FilterBarElementKey): string => {
    const mode = selectorWidthModes[key] === 'narrow' ? 'narrow' : 'full';
    return `${FILTER_WIDTHS[key][mode]}px`;
  };

  // Determine if we have space to show full reset text
  let showFullResetLabel = false;
  if (overflowFilters.length === 0) {
    const filtersBlockWidth = inlineSelectorKeys.reduce((sum: number, key: FilterBarElementKey, idx: number) => {
      const mode = selectorWidthModes[key] === 'narrow' ? 'narrow' : 'full';
      const gaps = idx === 0 ? 0 : GAP_BETWEEN_FILTERS;
      return sum + FILTER_WIDTHS[key][mode] + gaps;
    }, 0);
    const remaining =
      containerWidth - SETTINGS_ICON_WIDTH - RESET_BUTTON_MIN_WIDTH - GAP_BETWEEN_FILTERS - filtersBlockWidth;
    showFullResetLabel = remaining > RESET_BUTTON_FULL_WIDTH - RESET_BUTTON_MIN_WIDTH;
  }

  return (
    <Container
      ref={setContainerRef}
      className={TestSelectors.TEST_FILTER_BAR}
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        backgroundColor: '#FFFFFF',
        padding: '3px 10px',
        ...props.style,
      }}
    >
      {/* Settings icon */}
      <Flex
        justifyContent="center"
        alignItems="center"
        style={{ width: '25px', height: '50px', marginLeft: '10px', marginRight: '10px' }}
      >
        <SettingsIcon />
      </Flex>

      {/* Inline filters */}
      <Flex gap="20px" flex="1 1 auto" alignItems="center" style={{ minWidth: 0 }}>
        {inlineFilters.map(
          (f: { key: FilterBarElementKey; element: JSX.Element }): JSX.Element => (
            <FilterChildContainer
              key={f.key}
              style={{
                border: 0,
                backgroundColor: theme.colors.neutral50,
                flexShrink: 0,
                width: getInlineFilterWidth(f.key),
                height: '40px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {f.element}
            </FilterChildContainer>
          ),
        )}
      </Flex>

      {/* Reset / Overflow controls */}
      {overflowFilters.length === 0 ? (
        <ResetButtonInline
          showLabel={showFullResetLabel}
          onClick={() => {
            queryFilters.resetFiltersStable();
          }}
        />
      ) : (
        <OverflowMenu
          overflowFilters={overflowFilters.slice().map(
            (f: { key: FilterBarElementKey; element: JSX.Element }): JSX.Element => (
              <FilterChildContainer
                key={f.key}
                style={{
                  border: 0,
                  backgroundColor: theme.colors.neutral50,
                  width: '100%', // Make the container full-width
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center', // Vertically center the content Box
                }}
              >
                <Box width="100%">{f.element}</Box> {/* Ensure f.element has a full-width slot */}
              </FilterChildContainer>
            ),
          )}
          onReset={() => {
            queryFilters.resetFiltersStable();
          }}
        />
      )}
    </Container>
  );
}

const FilterChildContainer = styled(GroupChildContainer)`
  margin: 0;
  padding: 0;
  align-content: center;
  background-color: white !important;
`;

const Container = styled(ChartGroupContainer)`
  box-shadow: 0px 2px 20px 0px rgba(41, 54, 81, 0.15);
`;

interface OverflowMenuProps {
  overflowFilters: JSX.Element[];
  onReset: () => void;
}

function OverflowMenu({ overflowFilters, onReset }: OverflowMenuProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <Popover
      placement="bottom-end"
      trigger="click"
      isLazy
      isOpen={isMenuOpen}
      onOpen={() => {
        setIsMenuOpen(true);
      }}
      onClose={() => {
        setIsMenuOpen(false);
      }}
    >
      <PopoverTrigger>
        <Button
          aria-label="Open filter menu"
          variant="outline"
          size="md"
          width="40px"
          height="40px"
          p={0}
          minW="auto"
          mr="10px"
          bg={isMenuOpen ? theme.colors.neutral200 : theme.colors.neutral50}
          borderColor={isMenuOpen ? theme.colors.blue500 : theme.colors.neutral300}
          boxShadow={isMenuOpen ? 'outline' : 'none'}
          _hover={{
            bg: theme.colors.neutral100,
            borderColor: theme.colors.neutral400,
          }}
          _active={{
            bg: theme.colors.neutral200,
            borderColor: theme.colors.blue500,
          }}
        >
          <FontAwesomeIcon icon={faEllipsisH} />
        </Button>
      </PopoverTrigger>
      <PopoverContent padding="10px" minW="240px" boxShadow="outline">
        <PopoverBody>
          <Stack spacing="10px" width="100%">
            {overflowFilters}
            <Flex justifyContent="flex-end" width="100%">
              <ResetButtonContainer onClick={onReset} theme={theme}>
                <Flex gap="10px" alignItems="center" pl="8px" pr="8px">
                  <UndoIcon />
                  Reset
                </Flex>
              </ResetButtonContainer>
            </Flex>
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

const ResetButtonContainer = styled(GroupChildContainer)`
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  background-color: ${(props: ContainerWithThemeProps) => props.theme.colors.neutral50};
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${(props: ContainerWithThemeProps) => props.theme.colors.neutral100};
  }
`;

interface ResetButtonInlineProps {
  showLabel: boolean;
  onClick: () => void;
}

function ResetButtonInline({ showLabel, onClick }: ResetButtonInlineProps) {
  return (
    <Button
      aria-label="Reset filters"
      variant="outline"
      size="md"
      width={showLabel ? 'auto' : '40px'}
      height="40px"
      p={0}
      minW="auto"
      mr="10px"
      bg={theme.colors.neutral50}
      borderColor={theme.colors.neutral300}
      _hover={{ bg: theme.colors.neutral100, borderColor: theme.colors.neutral400 }}
      _active={{ bg: theme.colors.neutral200, borderColor: theme.colors.blue500 }}
      onClick={onClick}
    >
      <Flex gap="8px" alignItems="center" pl={showLabel ? '8px' : 0} pr={showLabel ? '8px' : 0}>
        <UndoIcon />
        {showLabel && <Text fontWeight="normal">Reset</Text>}
      </Flex>
    </Button>
  );
}
